import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Loader from "Common/Loader";
import DataGrid, {
    Pager,
    Paging,
    SearchPanel,
    Column,
    Summary,
    TotalItem,
    Toolbar,
    Item,
    Scrolling,
} from "devextreme-react/cjs/data-grid";
import { Card, Pagination } from "react-bootstrap";
import moment from 'moment';
import CustomStore from 'devextreme/data/custom_store';
import DateRangeBox from 'devextreme-react/date-range-box';
import TextBox, { type TextBoxTypes } from 'devextreme-react/text-box';
import { Button } from 'devextreme-react/button';
import { FaFileExcel } from "react-icons/fa";
import { GetAllCustomOrderGrid } from '../service';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import type { ValueChangedEvent } from 'devextreme/ui/date_range_box';
import ActionModal from './ActionModal';
import { reloadResources } from 'i18next';
import { useLocation } from 'react-router-dom';


const CustomOrderList = () => {
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ pageSize: 10, pageNumber: 1 });

    const now = new Date();
    const msInDay = 1000 * 60 * 60 * 24;
    const initialValue: [Date, Date] = [
        new Date(now.getTime() - msInDay * 3),
        new Date(now.getTime() + msInDay * 3),
    ];
    const [salesInventoryData, setSalesInventoryData] = useState<any[]>()
    const dataGridRef = useRef<DataGrid>(null);
    const [selectedRange, setSelectedRange] = useState<[Date, Date] | null>(initialValue);
    const [searchText, setSearchText] = useState('');
    const [permissionDetails, setPermissionDetails] = useState<string[]>([]);

    const HasPermission = (permission: string): boolean => {
        return permissionDetails.includes(permission);
    };

    function formatDateTime(date: Date): string {
        const pad = (n: number) => n.toString().padStart(2, '0');
        const day = pad(date.getDate());
        const month = pad(date.getMonth() + 1);
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
    }



    const dataSource = new CustomStore({
        key: 'id',
        load: async (loadOptions) => {
            try {
                const search = JSON.parse(JSON.stringify(loadOptions));
                let parameterList = new URLSearchParams();
                for (const [key, value] of Object.entries(search)) {
                    parameterList.append(key, JSON.stringify(value));
                }
                if (selectedRange) {
                    parameterList.append("FromDate", formatDateTime(selectedRange[0]));
                    parameterList.append("ToDate", formatDateTime(selectedRange[1]));

                }

                if (searchText) {
                    parameterList.append("Search", searchText);
                }
                const response = await GetAllCustomOrderGrid(parameterList);

                // return {
                //     data: response?.data || [],
                //     totalCount: response?.data.length || 0,
                // };

                return response;

            } catch (error) {
                console.error("Data loading error:", error);
                return { data: [], totalCount: 0 };
            }
        }
    });


    const getAllSalesInventoryDataList = async () => {
        try {
            let parameterList = new URLSearchParams();

            if (selectedRange) {
                parameterList.append("FromDate", formatDateTime(selectedRange[0]));
                parameterList.append("ToDate", formatDateTime(selectedRange[1]));

            }

            if (searchText) {
                parameterList.append("Search", searchText);
            }
            const response = await GetAllCustomOrderGrid(parameterList);
            setSalesInventoryData(response.data)
        } catch (error) {
            console.error('failde to load Customer Order List', error)
        }
    }
    useEffect(() => {
        getAllSalesInventoryDataList()
    }, [selectedRange]);

    const handleExportExcel = () => {
        if (!salesInventoryData) return;

        const worksheet = XLSX.utils.json_to_sheet(salesInventoryData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Errors");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, "Sales_Inventory_Export.xlsx");
    }

    const onCurrentValueChange = useCallback((e: ValueChangedEvent) => {
        const value = e.value as [Date, Date] | undefined;
        if (value && value[0] && value[1]) {
            const [startDate, endDate] = value;
            setSelectedRange([startDate, endDate]);
        } else {
            setSelectedRange(null);
        }
    }, []);
    const formatDate = (dateString: string): string => {

        const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
        const date = new Date(utcDateString);

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        const formattedHours = String(hours).padStart(2, '0');

        const formattedDate = `${day}/${month}/${year}`;
        const formattedTime = `${formattedHours}:${minutes}:${seconds} ${ampm}`;

        const fullFormatted = `${formattedDate} ${formattedTime}`;

        return fullFormatted;
    };

    const [selectedRecord, setSelectedRecord] = useState<any>(null);
    const [autoOpen, setAutoOpen] = useState(false);

    const location = useLocation();
    const openFirstRecord = location.state?.openFirstRecord;
    const [hasOpenedFirstRecord, setHasOpenedFirstRecord] = useState(false);

    useEffect(() => {
        if (location.state?.openFirstRecord) {
            window.history.replaceState({}, document.title);
        }
    }, []);
    return (
        <>
            {loading && <Loader />}
            <div className="page-content" style={{ maxWidth: '100vw', overflowX: 'auto' }}>
                <div className="container-fluid" >
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="m-0">Make To Order</h5>
                        </Card.Header>

                        <Card.Body>
                            <DataGrid
                                dataSource={dataSource}
                                showRowLines={true}
                                keyExpr="id"
                                ref={dataGridRef}
                                showBorders={true}
                                columnAutoWidth={true}
                                columnHidingEnabled={false}
                                width="100%"
                  remoteOperations={{ groupPaging: true }}

                                onContentReady={(e) => {
                                    const hasData = e.component.totalCount() > 0;
                                    e.component.option("pager.visible", hasData);
                                    e.component.option("paging.visible", hasData);
                                    if (openFirstRecord && !hasOpenedFirstRecord) {
                                        const visibleRows = e.component.getVisibleRows();
                                        if (visibleRows.length > 0) {
                                            const firstRowData = visibleRows[0]?.data;
                                            if (firstRowData) {
                                                setSelectedRecord(firstRowData);
                                                setAutoOpen(true);
                                                setHasOpenedFirstRecord(true);
                                            }
                                        }
                                    }
                                }}
                            >
                                <Scrolling
                                    mode='standard'
                                    useNative={true}
                                    scrollByContent={true}
                                    scrollByThumb={true}
                                />
                                <Toolbar>


                                    <Item location="before">
                                        <div style={{ display: 'flex', flexDirection: 'column', transform: "translateY(-22px)", zIndex: '122222' }}>
                                            <label>Search</label>
                                            <TextBox
                                                width={180}
                                                placeholder="Search..."
                                                value={searchText}
                                                onValueChanged={(e) => setSearchText(e.value)}
                                                onKeyDown={(e: any) => {
                                                    if (e.event.key === "Enter" || e.event.key === "Tab") {
                                                        dataGridRef.current?.instance.refresh();
                                                    }
                                                }}

                                            />

                                        </div>
                                    </Item>
                                    <Item location="before">
                                        <div style={{ transform: "translateY(-25px)" }}>
                                            {/* <DateRangeBox
                                                width={255}
                                                value={selectedRange || [null, null]}
                                                displayFormat="dd/MM/yyyy"
                                                onValueChanged={(e) => {
                                                    onCurrentValueChange(e);
                                                    dataGridRef.current?.instance.refresh();
                                                }}
                                            /> */}
                                            <DateRangeBox
                                                width={260}
                                                defaultValue={['', '']}
                                                displayFormat="dd/MM/yyyy"
                                                onValueChanged={onCurrentValueChange}
                                                showClearButton={true}
                                                acceptCustomValue={false}
                                            />
                                        </div>
                                    </Item>

                                </Toolbar>
                                <Paging defaultPageSize={10} />
                                <Pager
                                    showPageSizeSelector={true}
                                    allowedPageSizes={[10, 25, 50, 100]}
                                    showNavigationButtons={true}
                                    displayMode="full"
                                    showInfo={true}
                                />

                                <Summary>
                                    <TotalItem
                                    // column="id"
                                    // summaryType="count"
                                    // displayFormat="Total Records: {0}"
                                    />
                                </Summary>
                                <Column
                                    dataField="action"
                                    caption="Action"
                                    dataType="string"
                                    cellRender={({ data }) => (
                                        <div className="d-flex justify-content-center align-items-center gap-2">
                                            <ActionModal
                                                autoOpen={selectedRecord?.id === data.id && autoOpen}
                                                receiptNo={data.estimatePaymentNo} selectedId={data.id} customerId={data.customerId} reloadDataSource={() => dataGridRef.current?.instance.refresh()} />
                                        </div>
                                    )}
                                />
                                <Column defaultSortOrder="desc" dataField="estimatePaymentNo" caption="Estimate No" dataType="string" alignment="center" />
                                <Column dataField="customerName" caption="Customer Name" dataType="string" />
                                <Column dataField="salesPersonName" caption="Sales Person" dataType="string" alignment="center" />
                                <Column
                                    dataField="createdOn"
                                    caption="Created On"
                                    dataType="date"
                                    width={200}
                                    calculateDisplayValue={(rowData) => formatDate(rowData.createdOn)}
                                />
                                <Column dataField="totalAmount" format="#,##0.00" caption="Total Amount" dataType="number" alignment="center" />
                                <Column dataField="paidAmount" format="#,##0.00" caption="Paid Amount " dataType="number" alignment="center" />
                                <Column dataField="goldFreezRate" format="#,##0.00" caption="Gold FreezRate" dataType="number" alignment="center" />
                                <Column dataField="discountFreezRate" format="#,##0.00" caption="Discount FreezRate" dataType="number" alignment="center" calculateDisplayValue={(rowData) =>
                                    rowData.discountFreezRate > 0 ? "true" : "false"
                                } />
                            </DataGrid>
                        </Card.Body>
                    </Card>
                </div>
            </div >
        </>
    );
};


export default CustomOrderList